import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowDown, Bot, Palette, Zap, Sparkles, MousePointerClick } from "lucide-react";
import { SITE, HERO_BADGES } from "../config/site";
import { EASE } from "./Reveal";

function ParticleField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf, w, h, particles = [];
    const mouse = { x: -9999, y: -9999 };

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const count = Math.min(80, Math.floor((w * h) / 24000));
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        a: Math.random() * 0.45 + 0.18,
        c: Math.random() > 0.5 ? "150,160,255" : "195,140,255",
      });
    }

    const onMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.vx += (Math.random() - 0.5) * 0.012;
        p.vy += (Math.random() - 0.5) * 0.012;
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 140 * 140 && d2 > 0.01) {
          const d = Math.sqrt(d2);
          p.vx += (dx / d) * 0.03;
          p.vy += (dy / d) * 0.03;
        }
        const sp = Math.hypot(p.vx, p.vy);
        if (sp > 0.55) {
          p.vx = (p.vx / sp) * 0.55;
          p.vy = (p.vy / sp) * 0.55;
        }
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -12) p.x = w + 12;
        if (p.x > w + 12) p.x = -12;
        if (p.y < -12) p.y = h + 12;
        if (p.y > h + 12) p.y = -12;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.c},${p.a})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    const parent = canvas.parentElement;
    parent.addEventListener("mousemove", onMouse);
    parent.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      parent.removeEventListener("mousemove", onMouse);
      parent.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="absolute inset-0 z-[1] h-full w-full"
      data-testid="hero-particle-field"
    />
  );
}

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 0.95, delay: 0.35 + i * 0.14, ease: EASE },
});

export default function Hero() {
  const sectionRef = useRef(null);

  const onMouseMove = (e) => {
    const el = sectionRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
    el.style.setProperty("--my", ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
  };

  return (
    <section
      id="home"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      className="noise relative flex min-h-screen items-center overflow-hidden pb-20 pt-32 lg:pt-24"
      data-testid="hero-section"
    >
      {/* backdrop layers */}
      <div className="absolute inset-0 z-0 bg-grid [mask-image:radial-gradient(ellipse_65%_60%_at_50%_38%,black,transparent)]" />
      <ParticleField />
      <div
        className="pointer-events-none absolute -left-40 top-1/4 z-0 h-[480px] w-[480px] rounded-full bg-indigo-600/25 blur-[130px] transition-transform duration-700 ease-out [transform:translate3d(calc(var(--mx,0)*30px),calc(var(--my,0)*22px),0)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-32 top-10 z-0 h-[440px] w-[440px] rounded-full bg-purple-600/20 blur-[130px] transition-transform duration-700 ease-out [transform:translate3d(calc(var(--mx,0)*-38px),calc(var(--my,0)*-26px),0)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute bottom-0 left-1/3 z-0 h-[300px] w-[500px] rounded-full bg-blue-500/10 blur-[120px]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.12fr_0.88fr] lg:gap-8">
        {/* ——— Left: copy ——— */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            className="glass mb-7 inline-flex items-center gap-2.5 rounded-full px-4 py-2"
            data-testid="hero-role-badge"
          >
            <Sparkles className="h-3.5 w-3.5 text-indigo-300" />
            <span className="text-gradient-animated font-mono text-[11px] font-semibold uppercase tracking-[0.28em]">
              AI Website Developer
            </span>
          </motion.div>

          <h1
            className="font-display text-[2.6rem] font-extrabold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-[4.35rem] xl:text-[4.9rem]"
            data-testid="hero-heading"
          >
            {SITE.headlineLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  {...lineAnim(i)}
                  className={`block ${i === SITE.highlightIndex ? "text-gradient glow-soft" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-6 max-w-lg text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-description"
          >
            {SITE.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#demos"
              data-testid="hero-cta-view-work"
              className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white"
            >
              View My Work
              <ArrowDown className="h-4 w-4" />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="btn-ghost inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white"
            >
              Contact Me
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-2.5"
          >
            {HERO_BADGES.map((b, i) => {
              const Icon = [Bot, Palette, Zap][i];
              return (
                <span
                  key={b}
                  data-testid={`hero-badge-${i}`}
                  className="glass inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-300"
                >
                  <Icon className="h-3.5 w-3.5 text-indigo-300" />
                  {b}
                </span>
              );
            })}
          </motion.div>
        </div>

        {/* ——— Right: profile glass card ——— */}
        <motion.div
          initial={{ opacity: 0, scale: 0.88, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.7, ease: EASE }}
          className="relative mx-auto w-fit"
          data-testid="hero-profile-card"
        >
          <div
            className="absolute -inset-12 rounded-full bg-[radial-gradient(circle,rgba(129,140,248,0.22),transparent_65%)] blur-2xl animate-pulse-glow"
            aria-hidden="true"
          />
          <div className="relative h-[280px] w-[280px] sm:h-[340px] sm:w-[340px]">
            {/* rotating glow ring */}
            <div
              className="absolute -inset-[3px] animate-spin-slow rounded-full"
              style={{
                background:
                  "conic-gradient(from 0deg, #818cf8, #a855f7, #1e1b4b, #6d7cff, #818cf8)",
              }}
              aria-hidden="true"
            />
            <div className="glass-strong absolute inset-0 flex items-center justify-center rounded-full p-3">
              <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#0d1020] to-[#141834]">
                {SITE.profileImage ? (
                  <img
                    src={SITE.profileImage}
                    alt="Govind Developer — professional AI Website Developer"
                    className="h-full w-full object-cover"
                    data-testid="hero-profile-image"
                  />
                ) : (
                  <span
                    className="text-gradient font-display text-7xl font-extrabold sm:text-8xl"
                    data-testid="hero-profile-monogram"
                  >
                    GD
                  </span>
                )}
              </div>
            </div>

            {/* floating chips */}
            <div className="glass-strong absolute -right-8 top-6 hidden animate-float items-center gap-2 rounded-2xl px-4 py-2.5 sm:flex">
              <Sparkles className="h-4 w-4 text-purple-300" />
              <span className="text-xs font-semibold text-white">AI Powered</span>
            </div>
            <div
              className="glass-strong absolute -left-10 bottom-16 hidden animate-float items-center gap-2 rounded-2xl px-4 py-2.5 sm:flex"
              style={{ animationDelay: "1.4s" }}
            >
              <Zap className="h-4 w-4 text-indigo-300" />
              <span className="text-xs font-semibold text-white">Fast Delivery</span>
            </div>
            <div
              className="glass-strong absolute -bottom-4 right-10 hidden animate-float items-center gap-2 rounded-2xl px-4 py-2.5 sm:flex"
              style={{ animationDelay: "2.6s" }}
            >
              <MousePointerClick className="h-4 w-4 text-purple-300" />
              <span className="text-xs font-semibold text-white">Conversion Focused</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.a
        href="#about"
        aria-label="Scroll to about section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-7 left-1/2 z-10 hidden -translate-x-1/2 md:block"
        data-testid="hero-scroll-cue"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="glass flex h-11 w-7 items-start justify-center rounded-full p-2"
        >
          <div className="h-2 w-1 rounded-full bg-indigo-300" />
        </motion.div>
      </motion.a>
    </section>
  );
}
